import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LOGO_URL, LOGIN_BG } from "@/lib/constants";
import { Turnstile, TURNSTILE_SITE_KEY } from "@/components/Turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const tsRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, token);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in.");
      // Turnstile tokens are single-use — request a fresh one for the next attempt.
      setToken("");
      tsRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh]">
      {/* Left: form — centers when it fits, scrolls when taller than the viewport; never clips */}
      <div className="flex w-full flex-col px-6 py-8 sm:px-12 lg:w-[46%] lg:px-20">
        <div className="mx-auto my-auto w-full max-w-sm">
          <img src={LOGO_URL} alt="RetroTrack" className="mb-6 h-16 w-auto object-contain sm:h-20" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Coordination Portal</p>
          <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            Site Visit Templates
          </h1>
          <p className="mt-2 text-base text-slate-500">
            Sign in to record Mid & Post Coordination retrofit site visits.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@cphretrofit.co.uk"
                className="field-lg"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="field-lg"
                data-testid="login-password"
              />
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Turnstile ref={tsRef} onToken={setToken} action="login" className="mt-1" />

            <Button
              type="submit"
              disabled={loading || (!!TURNSTILE_SITE_KEY && !token)}
              className="field-lg w-full bg-accent text-base font-bold hover:bg-accent/90"
              data-testid="login-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="h-4 w-4" />
            PAS 2035 aligned coordination records
          </div>
        </div>
      </div>

      {/* Right: image */}
      <div className="relative hidden lg:block lg:w-[54%]">
        <img src={LOGIN_BG} alt="Engineer on site" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/80 via-slate-900/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-extrabold leading-tight">
            Consistent, compliant, in-depth site records.
          </h2>
          <p className="mt-3 max-w-md text-white/80">
            Capture measures, workmanship, photos and handover checks — then export a full PDF report from site.
          </p>
        </div>
      </div>
    </div>
  );
}
